import { useState } from 'react';
import {
  Alert, Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { ApiFallo, ResumenCreditos, TransferStats } from '../lib/api.js';
import { formatearDuracion, formatearHace, formatearTamano, formatearVelocidad } from '../lib/eventos.js';
import { GraficaVelocidad } from '../components/GraficaVelocidad.js';
import { bandera } from '../components/DetalleAmule.js';

/** Cuánta historia se pide. Dos días es el tope porque aMule no guarda más: su historia
 *  vive en memoria y arranca vacía con él. */
const VENTANAS = [
  { segundos: 30 * 60, etiqueta: '30 min' },
  { segundos: 2 * 3600, etiqueta: '2 h' },
  { segundos: 12 * 3600, etiqueta: '12 h' },
  { segundos: 2 * 86_400, etiqueta: '2 días' },
];

/** El ratio como lo enseña aMule: «1 : 2,35» es «por cada byte subido, 2,35 bajados». Por
 *  debajo de 1 se da la vuelta —«1,60 : 1»— para que el número grande siga a la izquierda
 *  y se lea igual: subes más de lo que bajas. */
export function formatearRatio(r: number | null): string {
  if (r == null) return 'sin datos';
  return r >= 1 ? `1 : ${r.toFixed(2)}` : `${(1 / r).toFixed(2)} : 1`;
}

export function Red() {
  const [ventana, setVentana] = useState(VENTANAS[0]!.segundos);

  const estadisticas = useQuery({
    queryKey: ['amule-stats'],
    queryFn: api.estadisticasAmule,
    refetchInterval: 10_000,
    retry: false,
  });
  const historia = useQuery({
    queryKey: ['amule-velocidades', ventana],
    queryFn: () => api.historialVelocidad(ventana),
    refetchInterval: 5_000,
    retry: false,
    // Al cambiar de ventana se deja la gráfica de antes hasta que llegue la nueva: sin
    // esto la tarjeta se vacía y vuelve a llenarse, y la página da un salto.
    placeholderData: keepPreviousData,
  });

  const fallo = (estadisticas.error ?? historia.error) as ApiFallo | null;
  if (fallo) {
    return (
      <Stack spacing={2}>
        <Typography variant="h5">Red</Typography>
        <Alert severity={fallo.code === 'amule_unconfigured' ? 'info' : 'error'}>
          {fallo.code === 'amule_unconfigured'
            ? 'aMule no está configurado: sin contraseña de amuleapi no hay nada que contar aquí.'
            : fallo.message}
        </Alert>
      </Stack>
    );
  }

  const e = estadisticas.data?.estadisticas;
  const h = historia.data?.historia;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5">Red</Typography>
        <Typography variant="body2" color="text.secondary">
          Lo que aMule lleva contado y cómo van las velocidades. Es suyo: se le pregunta a él
          y se enseña tal cual, sin guardar nada por nuestra parte.
        </Typography>
      </Box>

      {e && <Resumen e={e} />}

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
          <Typography variant="subtitle1">Velocidad</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={ventana}
            onChange={(_e, v: number | null) => { if (v != null) setVentana(v); }}
            aria-label="Cuánta historia enseñar"
          >
            {VENTANAS.map((v) => (
              <ToggleButton key={v.segundos} value={v.segundos} sx={{ px: 1.5 }}>{v.etiqueta}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
        <GraficaVelocidad
          puntos={h?.points ?? []}
          ventanaSegundos={historia.data?.ventanaSegundos ?? ventana}
          atenuada={historia.isPlaceholderData}
        />
        {h && h.intervalSeconds > 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Cada punto es la media de {formatearDuracion(h.intervalSeconds)}. La historia es la
            que aMule guarda en memoria: empieza cuando arranca y no llega más atrás de lo que
            le cabe.
          </Typography>
        )}
      </Paper>

      {e && <Detalle e={e} />}

      <Creditos />
    </Stack>
  );
}

/** El almacén de créditos de aMule se pregunta aparte y más despacio: son miles de
 *  registros que amuleapi pagina de 500 en 500, y no cambian cada segundo. */
function Creditos() {
  const { data, error } = useQuery({
    queryKey: ['amule-creditos'],
    queryFn: api.clientesConCredito,
    refetchInterval: 30_000,
    retry: false,
  });
  if (error) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Con quién tienes crédito</Typography>
        <Alert severity="error">{(error as ApiFallo).message}</Alert>
      </Paper>
    );
  }
  if (!data) return null;
  return <TablaCreditos r={data} />;
}

/** El crédito como multiplicador, que es lo que es: «×3,2» se entiende; «3,2» no. */
const credito = (c: number) => `×${c.toFixed(1)}`;

function TablaCreditos({ r }: { r: ResumenCreditos }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1">Con quién tienes crédito</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        aMule recuerda {r.conocidos} cliente(s) con los que ha intercambiado datos. Con{' '}
        <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{r.conCredito}</Box>{' '}
        tienes crédito, y {r.conectados} de ellos están conectados ahora, que es cuando sirve.
      </Typography>

      {r.clientes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Todavía nadie te debe nada: el crédito empieza al superar 1 MB subido a un mismo
          cliente, y a partir de ahí crece con lo que le des.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell align="right">
                  <Tooltip title="Por cuánto multiplica tu tiempo de espera en su cola. Estimado con tus contadores: él lo calcula con los suyos.">
                    <span>Crédito</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Le has subido</TableCell>
                <TableCell align="right">Te ha dado</TableCell>
                <TableCell align="right">Visto</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {r.clientes.map((c) => (
                <TableRow key={c.userHash}>
                  <TableCell>
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                      <span>{bandera(c.countryCode)}</span>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                          {c.name || <Box component="span" sx={{ color: 'text.secondary' }}>sin nombre</Box>}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {[c.software, c.softwareVersion].filter(Boolean).join(' ') || 'cliente desconocido'}
                        </Typography>
                      </Box>
                      {c.online && <Chip size="small" color="success" label="conectado" />}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {credito(c.credito)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatearTamano(c.uploadedBytes)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatearTamano(c.downloadedBytes)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{c.online ? 'ahora' : formatearHace(c.lastSeen)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* La fórmula, con todas las letras: sin ella el multiplicador parece arbitrario, y
          es justo lo que explica por qué subir a un mismo cliente vale más que repartir. */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        El crédito es por cliente y lo calcula él con lo que le has subido: el menor entre
        el doble de lo que le diste partido por lo que te dio, y la raíz de los megas que le
        diste más dos, entre ×1 y ×10, y nada por debajo de 1 MB. Aquí se estima con tus
        contadores, y solo cuenta con quien te identifica de forma segura. Un ×3 con alguien
        que no comparte lo que buscas no adelanta nada.
      </Typography>
    </Paper>
  );
}

/** Las cuatro cifras que responden a la pregunta de esta pantalla: cuánto he dado, cuánto he
 *  recibido, en qué proporción y desde cuándo. */
function Resumen({ e }: { e: TransferStats }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
      <Cifra
        etiqueta="Subido"
        valor={formatearTamano(e.session.uploadBytes)}
        nota={`${formatearTamano(e.total.uploadBytes)} desde siempre`}
      />
      <Cifra
        etiqueta="Recibido"
        valor={formatearTamano(e.session.downloadBytes)}
        nota={`${formatearTamano(e.total.downloadBytes)} desde siempre`}
      />
      <Cifra
        etiqueta="Ratio subida : bajada"
        valor={formatearRatio(e.session.ratio)}
        nota={`${formatearRatio(e.total.ratio)} desde siempre`}
      />
      <Cifra
        etiqueta="aMule en marcha desde hace"
        valor={formatearDuracion(e.uptimeSeconds)}
        nota="Las cifras de sesión cuentan desde entonces"
      />
    </Box>
  );
}

function Cifra({ etiqueta, valor, nota }: { etiqueta: string; valor: string; nota: string }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{etiqueta}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 600, my: 0.25 }}>{valor}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{nota}</Typography>
    </Paper>
  );
}

function Detalle({ e }: { e: TransferStats }) {
  const media = e.rates;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>La cola de subida</Typography>
        <Stack spacing={1}>
          <Fila etiqueta="Recibiendo de ti ahora" valor={`${e.uploads.active} cliente(s)`} />
          <Fila etiqueta="Esperando turno" valor={`${e.uploads.waiting} cliente(s)`} />
          <Fila etiqueta="Trozos servidos enteros" valor={String(e.uploads.successful)} />
          <Fila etiqueta="Subidas cortadas a medias" valor={String(e.uploads.failed)} />
        </Stack>
        {/* La pregunta que trae aquí a la gente es «¿qué puntuación tengo?», y no existe
            como número: hay que decirlo, o se busca en otra pantalla. */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          En eD2k no hay una puntuación tuya que consultar: cada cliente al que le subes te
          apunta un crédito en SU disco y lo usa para adelantarte en su cola. Lo que sí se
          mide desde aquí es lo que das y lo que recibes, y el ratio de arriba es esa
          proporción: «1 : 2» es bajar el doble de lo que subes.
        </Typography>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Esta sesión</Typography>
        <Stack spacing={1}>
          <Fila etiqueta="Bajada media" valor={formatearVelocidad(media.avgDownloadBps)} />
          <Fila etiqueta="Bajada máxima" valor={formatearVelocidad(media.maxDownloadBps)} />
          <Fila etiqueta="Subida media" valor={formatearVelocidad(media.avgUploadBps)} />
          <Fila etiqueta="Subida máxima" valor={formatearVelocidad(media.maxUploadBps)} />
          <Fila etiqueta="Trozos bajando ahora" valor={String(e.downloads.activeChunks)} />
          <Fila etiqueta="Fuentes localizadas" valor={String(e.downloads.foundSources)} />
          <Fila
            etiqueta="Compartiendo"
            valor={`${e.shared.files} fichero(s), ${formatearTamano(e.shared.bytes)}`}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          La media es lo movido entre el tiempo en marcha, así que baja sola mientras no se
          transfiere nada.
        </Typography>
      </Paper>
    </Box>
  );
}

function Fila({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">{etiqueta}</Typography>
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</Typography>
    </Stack>
  );
}
